import { Callout } from '@dev-spendesk/grapes';
import { useMemo } from 'react';

import { useCompany } from 'modules/app/hooks/useCompany';
import { QueryError } from 'src/core/common/components/QueryError';
import { QuerySuspense } from 'src/core/common/components/QuerySuspense';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { convertToCompanyCurrency, formatMoney } from 'src/core/utils/money';

import {
  getCalloutTitleKey,
  getCalloutDescriptionKey,
} from './calloutTranslationHelpers';
import { useSpendLimitStatusQuery } from '../../hooks/useSpendLimitStatusQuery';
import { type SpendLimitPeriodStatus } from '../../models/spendLimit';

export const StatusByCategoryLimitCallout = ({
  requestDate,
  currency,
  requestAmount,
  expenseCategoryId,
  className,
}: {
  requestDate: Date;
  currency: string;
  requestAmount: number;
  expenseCategoryId: string;
  className?: string;
}) => {
  const { t } = useTranslation('global');
  const company = useCompany();
  const spendLimitStatusQueryState = useSpendLimitStatusQuery({
    requestDate,
    expenseCategoryId,
    enabled: true,
  });

  const requestAmountCalculated = useMemo(() => {
    return convertToCompanyCurrency(requestAmount, currency, company);
  }, [requestAmount, currency, company, spendLimitStatusQueryState]);

  // TODO: handle 404 specifically
  if (spendLimitStatusQueryState.status === 'error') {
    return null;
  }

  return (
    <QuerySuspense
      loading={null}
      queryState={spendLimitStatusQueryState}
      fallback={(error) => (
        <QueryError queryError={error} componentType="Callout" />
      )}
    >
      {({ spendLimitStatus }) => {
        const { exceededStatuses, withinLimitStatuses } =
          spendLimitStatus.reduce(
            (
              accumulator: {
                exceededStatuses: SpendLimitPeriodStatus[];
                withinLimitStatuses: SpendLimitPeriodStatus[];
              },
              status,
            ) => {
              const { usedAmount, limitAmount } = status;
              if (usedAmount + requestAmountCalculated > limitAmount) {
                accumulator.exceededStatuses.push(status);
              } else {
                accumulator.withinLimitStatuses.push(status);
              }
              return accumulator;
            },
            {
              exceededStatuses: [],
              withinLimitStatuses: [],
            },
          );

        return (
          <>
            {/* Show warning callout for exceeded limits (highest priority) */}
            {exceededStatuses.length > 0 && (
              <Callout
                className={className}
                variant="warning"
                title={t(
                  'spendLimits.modal.request.calloutWarning.limitExceeded',
                )}
              >
                <ul className="list-inside list-disc space-y-2">
                  {exceededStatuses.map(
                    ({ period, limitAmount, currency: limitCurrency }) => {
                      const calloutDescriptionKey =
                        getCalloutDescriptionKey(period);
                      return (
                        <li key={`exceeded-${period}`}>
                          {t(calloutDescriptionKey, {
                            amount: formatMoney(limitAmount, limitCurrency),
                          })}
                        </li>
                      );
                    },
                  )}
                </ul>
              </Callout>
            )}

            {/* Show info callout for within-limit statuses */}
            {withinLimitStatuses.length > 0 && (
              <Callout
                className={className}
                variant="info"
                title={t('spendLimits.modal.request.calloutInfo.title')}
              >
                <ul className="list-inside list-disc space-y-2">
                  {withinLimitStatuses.map(
                    ({ period, limitAmount, currency: limitCurrency }) => {
                      const calloutTitleKey = getCalloutTitleKey(period);
                      return (
                        <li key={`within-limit-${period}`}>
                          {t(calloutTitleKey, {
                            amount: formatMoney(limitAmount, limitCurrency),
                          })}
                        </li>
                      );
                    },
                  )}
                </ul>
              </Callout>
            )}
          </>
        );
      }}
    </QuerySuspense>
  );
};
